import { FocusEvent, HTMLAttributes, memo, ReactNode, useCallback, useMemo } from "react";
import classnames from "classnames";
import { DropzoneOptions, useDropzone } from "react-dropzone";
import { UploadIcon } from "./icon";

import styles from "./style.module.scss";

export type FileUploadBaseProps = DropzoneOptions & HTMLAttributes<HTMLDivElement> & {
  id?: string,
  name?: string,
  logo?: ReactNode,
  value?: File | File[] | null,
  onBlur?: (e: FocusEvent<HTMLDivElement>) => void,
  onChange?: (files: File | File[] | null) => void,
  appendFiles?: boolean,
  supportingText?: string,
  iconClassName?: string,
  inputClassName?: string,
  titleClassName?: string,
  messageClassName?: string,
  subtitleClassName?: string,
  messageClickTitleClassName?: string,
}

export const FileUploadBase = memo<FileUploadBaseProps>(
  (
    {
      id,
      logo,
      name,
      value,
      onBlur,
      onDrop,
      onChange,
      supportingText,
      className,
      iconClassName,
      inputClassName,
      titleClassName,
      messageClassName,
      subtitleClassName,
      messageClickTitleClassName,
      appendFiles,
      ...props
    }) => {
    const arrayValue = useMemo(() => value ? (Array.isArray(value) ? value : [ value ]) : [], [ value ]);
    const wrappedOnDrop = useCallback((acceptedFiles: File | File[]) => {
      const files = Array.isArray(acceptedFiles) ? acceptedFiles : [ acceptedFiles ];
      const combinedFiles = props.multiple
        ? (appendFiles ? [ ...arrayValue, ...files ] : acceptedFiles)
        : files?.[0];

      onChange?.(combinedFiles);
    }, [ onChange, arrayValue, props.multiple, appendFiles ]);

    const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
      useFsAccessApi: false,
      onDrop: wrappedOnDrop,
      ...props,
    });

    const { onBlur: rootOnBlur, ...rootProps } = getRootProps();
    const inputProps = getInputProps();
    const wrappedOnBlur = useCallback((e: FocusEvent<HTMLDivElement>) => {
      rootOnBlur?.(e);
      onBlur?.(e);
    }, [ rootOnBlur, onBlur ]);

    return (
      <div
        {...rootProps}
        id={id}
        onBlur={wrappedOnBlur}
        className={classnames(className, styles.root, {
          [styles.reject]: isDragReject,
          [styles.accept]: isDragAccept,
          [styles.disabled]: props.disabled,
        })}
      >
        <input
          name={name}
          className={classnames(inputClassName, styles.input)}
          {...inputProps}
        />
        <div className={classnames(messageClassName, styles.message)}>
          <i className={classnames(iconClassName, styles.icon)}>
            {logo ? logo : <UploadIcon />}
          </i>
          {
            isDragActive ?
              <>
                <p className={classnames(titleClassName, styles.messageTitle)}>
                  {
                    isDragReject
                      ? "Some files are not supported and will be ignored"
                      : "Drop files here ..."
                  }
                </p>
              </>
              :
              <>
                <p className={classnames(titleClassName, styles.messageTitle)}>
                  <span className={classnames(messageClickTitleClassName, styles.messageClickTitle)}> Click to upload </span>
                  or drag and drop
                </p>
                <p className={classnames(subtitleClassName, styles.messageSubTitle)}>{supportingText}</p>
              </>
          }
        </div>
      </div>
    );
  });
